/**
 * Hook para monitoramento das filas PGMQ de webhooks (routing e deals)
 * 
 * Usa as RPCs get_webhook_queue_stats_pgmq, get_webhook_queue_items_pgmq
 * e get_webhook_queue_volume_pgmq para consultar estatísticas do PGMQ
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Tipos de fila suportadas */
export type WebhookQueueType = 'routing' | 'deals';

/** Interface para estatísticas da fila PGMQ */
export interface WebhookQueueStats {
  pending: number;
  processing: number;
  completed24h: number;
  failed24h: number;
  dlqCount: number;
  successRate: number;
  avgProcessingTime: number;
}

/** Interface para item da fila PGMQ */
export interface WebhookQueueItem {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: 'pending' | 'processing' | 'archived' | 'dlq';
  tenant_id: string | null;
  source: 'queue' | 'archive' | 'dlq';
  // Campos enriquecidos
  tenant_name?: string;
  phone?: string;
  // Campos extraídos do payload para exibição
  provider_slug?: string | null;
  queue_type?: string;
  flow_name?: string | null;
  processing_time?: number; // em segundos
}

/** Interface para volume por hora */
export interface WebhookQueueVolume {
  hour: string;
  completed: number;
  failed: number;
}

/** Interface para alerta de saúde */
export interface WebhookQueueAlert {
  id: string;
  type: 'warning' | 'error';
  message: string;
  count?: number;
}

// ============================================================================
// INTERFACES DE RESPOSTA DAS RPCS
// ============================================================================

interface WebhookQueueStatsRpcResponse {
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  dlq_count: number;
  avg_latency_ms: number;
}

interface WebhookQueueItemRpcResponse {
  msg_id: number;
  read_ct: number;
  enqueued_at: string;
  vt: string;
  message: Record<string, unknown>;
  status: string;
  tenant_id: string | null;
  source: string;
}

interface WebhookQueueVolumeRpcResponse {
  hour_label: string;
  completed_count: number;
  failed_count: number;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para buscar estatísticas da fila de webhooks
 * Usa RPC get_webhook_queue_stats_pgmq
 */
export function useWebhookQueueStats(queue: WebhookQueueType) {
  return useQuery({
    queryKey: ['webhook-queue-stats', queue],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_webhook_queue_stats_pgmq', {
        p_queue: queue
      });
      
      if (error) throw error;
      
      // RPC retorna array com um elemento
      const stats = (data as unknown as WebhookQueueStatsRpcResponse[])?.[0];
      
      if (!stats) {
        return {
          pending: 0,
          processing: 0,
          completed24h: 0,
          failed24h: 0,
          dlqCount: 0,
          successRate: 100,
          avgProcessingTime: 0
        } as WebhookQueueStats;
      }
      
      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 
        ? (stats.completed_count / total) * 100 
        : 100;
      
      return {
        pending: stats.pending_count || 0,
        processing: stats.processing_count || 0,
        completed24h: stats.completed_count || 0,
        failed24h: stats.failed_count || 0,
        dlqCount: stats.dlq_count || 0,
        successRate,
        avgProcessingTime: (stats.avg_latency_ms || 0) / 1000 // converter ms para segundos
      } as WebhookQueueStats;
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Hook para buscar itens da fila de webhooks
 * Usa RPC get_webhook_queue_items_pgmq
 */
export function useWebhookQueueItems(
  queue: WebhookQueueType,
  statusFilter: string = 'all',
  limit: number = 200  // Limite aumentado para suportar paginação frontend
) {
  return useQuery({
    queryKey: ['webhook-queue-items', queue, statusFilter, limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_webhook_queue_items_pgmq', {
        p_queue: queue,
        p_status: statusFilter,
        p_limit: limit
      });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as WebhookQueueItemRpcResponse[];

      // Extrair tenant_ids únicos para enriquecimento
      const tenantIds = new Set<string>();
      rawItems.forEach(item => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
      });

      // Buscar nomes dos tenants
      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants')
          .select('id, name')
          .in('id', Array.from(tenantIds));
        
        tenantMap = new Map(
          (tenants || []).map((t: { id: string; name: string }) => [t.id, t.name])
        );
      }

      // Mapear itens com enriquecimento e campos extraídos do payload
      return rawItems.map(item => {
        // Calcular tempo de processamento para itens arquivados
        let processingTime: number | undefined;
        if (item.source === 'archive' && item.enqueued_at && item.vt) {
          const start = new Date(item.enqueued_at).getTime();
          const end = new Date(item.vt).getTime();
          processingTime = (end - start) / 1000; // converter para segundos
        }
        
        return {
          msg_id: item.msg_id,
          read_ct: item.read_ct,
          enqueued_at: item.enqueued_at,
          vt: item.vt,
          message: item.message,
          status: item.status as 'pending' | 'processing' | 'archived' | 'dlq',
          tenant_id: item.tenant_id,
          source: item.source as 'queue' | 'archive' | 'dlq',
          tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
          phone: (item.message?.phone as string) || (item.message?.contact_phone as string) || '',
          // Campos extraídos do payload
          provider_slug: (item.message?.provider_slug as string) || null,
          queue_type: (item.message?.type as string) || (item.message?.queue_type as string) || 'webhook',
          flow_name: (item.message?.flow_name as string) || null,
          processing_time: processingTime,
        };
      }) as WebhookQueueItem[];
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar volume de processamento por hora
 * Usa RPC get_webhook_queue_volume_pgmq
 */
export function useWebhookQueueVolume(queue: WebhookQueueType, hours: number = 24) {
  return useQuery({
    queryKey: ['webhook-queue-volume', queue, hours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_webhook_queue_volume_pgmq', {
        p_queue: queue,
        hours_ago: hours
      });
      
      if (error) throw error;
      
      return ((data || []) as unknown as WebhookQueueVolumeRpcResponse[]).map((item) => ({
        hour: item.hour_label,
        completed: item.completed_count || 0,
        failed: item.failed_count || 0
      })) as WebhookQueueVolume[];
    },
    refetchInterval: 60000,
  });
}

/**
 * Hook para gerar alertas de saúde da fila de webhooks
 */
export function useWebhookQueueAlerts(queue: WebhookQueueType) {
  return useQuery({
    queryKey: ['webhook-queue-alerts', queue],
    queryFn: async () => {
      const alerts: WebhookQueueAlert[] = [];
      
      // Buscar estatísticas
      const { data: statsData } = await supabase.rpc('get_webhook_queue_stats_pgmq', {
        p_queue: queue
      });
      const stats = (statsData as unknown as WebhookQueueStatsRpcResponse[])?.[0];
      
      if (!stats) return alerts;
      
      // Alerta de itens pendentes altos
      if (stats.pending_count > 50) {
        alerts.push({
          id: 'high-pending',
          type: 'warning',
          message: `${stats.pending_count} itens pendentes na fila ${queue}`,
          count: stats.pending_count
        });
      }
      
      // Alerta de itens em processamento por muito tempo
      if (stats.processing_count > 20) {
        alerts.push({
          id: 'high-processing',
          type: 'warning',
          message: `${stats.processing_count} itens em processamento (podem estar stuck)`,
          count: stats.processing_count
        });
      }
      
      // Alerta de itens na DLQ
      if (stats.dlq_count > 0) {
        alerts.push({
          id: 'dlq-items',
          type: 'error',
          message: `${stats.dlq_count} itens na DLQ (falhas permanentes)`,
          count: stats.dlq_count
        });
      }
      
      // Alerta de taxa de sucesso baixa
      const total = stats.completed_count + stats.failed_count;
      const successRate = total > 0 ? (stats.completed_count / total) * 100 : 100;
      
      if (successRate < 90 && total > 10) {
        alerts.push({
          id: 'low-success-rate',
          type: 'error',
          message: `Taxa de sucesso baixa: ${successRate.toFixed(1)}%`,
          count: stats.failed_count
        });
      }
      
      // Alerta de latência alta (>30 segundos = 30000ms)
      if (stats.avg_latency_ms > 30000) {
        alerts.push({
          id: 'high-latency',
          type: 'warning',
          message: `Latência média alta: ${(stats.avg_latency_ms / 1000).toFixed(0)}s`
        });
      }
      
      return alerts;
    },
    refetchInterval: 30000,
  });
}

// ============================================================================
// INTERFACE E HOOK PARA ITENS MAIS LENTOS
// ============================================================================

/** Interface para resposta da RPC de itens lentos */
interface WebhookQueueSlowestItemRpcResponse {
  msg_id: number;
  read_ct: number;  // NOVO: contador de tentativas de leitura
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  status: string;
  tenant_id: string | null;
  message: Record<string, unknown>;
  source: string;
}

/** Interface para item lento da fila (enriquecido) */
export interface WebhookQueueSlowestItem {
  msg_id: number;
  read_ct: number;              // Contador de tentativas de leitura
  enqueued_at: string;
  completed_at: string | null;
  processing_time_ms: number;
  processing_time_formatted: string;
  status: 'pending' | 'processing' | 'archived';
  tenant_id: string | null;
  tenant_name?: string;
  phone?: string;
  provider_slug?: string | null;
  queue_type?: string;          // Tipo da fila extraído de message
  flow_name?: string | null;    // Nome do flow extraído de message
  source: 'queue' | 'archive';
  message?: Record<string, unknown>;  // Payload completo para visualização
}

/**
 * Formata o tempo de processamento em uma string legível
 * Ex: "1.2s", "45.3s", "1m 23s", "2h 15m"
 */
function formatProcessingTimeMs(ms: number): string {
  const seconds = ms / 1000;
  
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Hook para buscar os TOP N itens com maior tempo de execução
 * Usa RPC get_webhook_queue_slowest_items_pgmq
 */
export function useWebhookQueueSlowestItems(
  queue: WebhookQueueType,
  limit: number = 20
) {
  return useQuery({
    queryKey: ['webhook-queue-slowest', queue, limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_webhook_queue_slowest_items_pgmq', {
        p_queue: queue,
        p_limit: limit
      });
      
      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      const rawItems = data as unknown as WebhookQueueSlowestItemRpcResponse[];
      
      // Extrair tenant_ids únicos para enriquecimento
      const tenantIds = new Set<string>();
      rawItems.forEach(item => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
      });

      // Buscar nomes dos tenants
      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants')
          .select('id, name')
          .in('id', Array.from(tenantIds));
        
        tenantMap = new Map(
          (tenants || []).map((t: { id: string; name: string }) => [t.id, t.name])
        );
      }
      
      // Mapear e enriquecer itens
      return rawItems.map(item => ({
        msg_id: item.msg_id,
        read_ct: item.read_ct || 0,
        enqueued_at: item.enqueued_at,
        completed_at: item.completed_at,
        processing_time_ms: item.processing_time_ms,
        processing_time_formatted: formatProcessingTimeMs(item.processing_time_ms),
        status: item.status as 'pending' | 'processing' | 'archived',
        tenant_id: item.tenant_id,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        phone: (item.message?.phone as string) || (item.message?.contact_phone as string) || '',
        provider_slug: (item.message?.provider_slug as string) || null,
        queue_type: (item.message?.type as string) || (item.message?.queue_type as string) || 'webhook',
        flow_name: (item.message?.flow_name as string) || null,
        source: item.source as 'queue' | 'archive',
        message: item.message || {},  // Payload completo para visualização
      })) as WebhookQueueSlowestItem[];
    },
    refetchInterval: 30000,
  });
}
